/**
 * ChatConversationView - Single conversation with messages
 * Shows messages, input, and handles sending
 */

import { useEffect, useRef, useState } from 'react'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { useChat } from '../../contexts/ChatContext'
import { useAuth } from '../../contexts/AuthContext'
import { useRealtimeMessages } from '../../hooks/useRealtimeMessages'
import ChatMessages from './ChatMessages'
import ChatInput from './ChatInput'
import ChatUnavailable from './ChatUnavailable'
import { getFirstNameOnly } from '../../lib/privacy/sanitizeTalentData'

interface ChatConversationViewProps {
  onBack: () => void
}

export default function ChatConversationView({ onBack }: ChatConversationViewProps) {
  const { currentBookingId, currentThreadId, activeChats, updateLastMessage, fetchActiveChats } = useChat()
  const { user, profile } = useAuth()
  const bookingChats = activeChats.filter(chat =>
    chat.type === 'booking' && chat.bookingId === currentBookingId
  )
  const resolvedThreadId = currentThreadId || (
    bookingChats.length === 1 ? bookingChats[0].threadId : null
  )
  const {
    messages,
    isLoading,
    error,
    isConnected,
    sendMessage,
    markAllAsRead
  } = useRealtimeMessages(currentBookingId!, resolvedThreadId)

  const [isSending, setIsSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const prevMessageCountRef = useRef(0)
  const isNearBottomRef = useRef(true)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Track whether user is near the bottom of the chat
  const handleScroll = () => {
    const container = scrollContainerRef.current
    if (!container) return
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight
    isNearBottomRef.current = distanceFromBottom < 120
  }

  // Get current chat info
  const currentChat = activeChats.find(chat =>
    chat.bookingId === currentBookingId && chat.threadId === resolvedThreadId
  )

  // Determine which name to show based on user role
  // Photographers see customer name, customers see photographer name
  const isPhotographer = profile?.role === 'photographer'
  const talentNames = [
    currentChat?.photographer?.full_name,
    currentChat?.videographer?.full_name,
    currentChat?.secondPhotographer?.full_name
  ].filter(Boolean).map((name) => getFirstNameOnly(name))
  const otherPersonName = isPhotographer
    ? (currentChat?.customer?.full_name
        ? getFirstNameOnly(currentChat.customer.full_name)
        : currentChat?.customer?.email
          ? currentChat.customer.email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
          : 'Client')
    : (talentNames.length > 0 ? talentNames.join(' + ') : 'Wedding Team')

  // Auto-scroll to bottom only when a new message is added AND user is near the bottom
  useEffect(() => {
    const newCount = messages.length
    if (newCount > prevMessageCountRef.current && isNearBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
    prevMessageCountRef.current = newCount
  }, [messages])

  // Messages from the other side that this reader has not seen yet. The mark-as-read effect
  // keys on this COUNT, not on the messages array: the array is replaced on every fetch and
  // every local update, and keying on it made the effect re-fire after each mark-as-read,
  // hitting the server every ~2 seconds for as long as the chat stayed open.
  const readerIsPhotographer = profile?.role === 'photographer'
  const unreadForMe = messages.filter((m: any) =>
    m.sender_id !== user?.id &&
    !(readerIsPhotographer ? m.is_read_photographer : m.is_read_customer)
  ).length

  // Mark messages as read when viewing, once per batch of unread messages
  useEffect(() => {
    if (unreadForMe === 0 || !currentBookingId) return

    const timer = setTimeout(async () => {
      const result = await markAllAsRead()
      if (result?.success) {
        // Refresh active chats to update unread counts in the UI
        fetchActiveChats()
        // Also dispatch event to notify NotificationBell to refetch
        window.dispatchEvent(new CustomEvent('chat-messages-read'))
      } else {
        console.warn('📖 ChatConversationView: markAllAsRead did not succeed', result)
      }
    }, 1000) // Wait 1 second before marking as read

    return () => clearTimeout(timer)
  }, [unreadForMe, currentBookingId, resolvedThreadId, markAllAsRead, fetchActiveChats])

  const handleSendMessage = async (messageText: string) => {
    if (!messageText.trim()) return

    setIsSending(true)
    try {
      const result = await sendMessage(messageText)
      if (result.success) {
        // Update last message in chat list
        updateLastMessage(currentBookingId!, new Date().toISOString(), messageText, resolvedThreadId)
      } else {
        console.error('Failed to send message:', result.error)
        // Show user-friendly error to the user
        alert(`Message not sent: ${result.error || 'Please try again or contact support.'}`)
      }
    } finally {
      setIsSending(false)
    }
  }

  // Check if chat is available
  const now = new Date()
  const chatVisibleFrom = currentChat?.chatVisibleFrom
    ? new Date(currentChat.chatVisibleFrom)
    : null
  const chatVisibleUntil = currentChat?.chatVisibleUntil
    ? new Date(currentChat.chatVisibleUntil)
    : null

  const isChatAvailable = chatVisibleFrom && chatVisibleUntil
    && now >= chatVisibleFrom
    && now <= chatVisibleUntil

  if (!currentBookingId) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        No chat selected
      </div>
    )
  }

  if (!resolvedThreadId || !currentChat) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-6 text-center text-gray-500">
        <p className="font-medium text-gray-700">Select a conversation</p>
        <button
          onClick={onBack}
          className="mt-3 text-sm text-primary hover:underline"
        >
          Back to messages
        </button>
      </div>
    )
  }

  if (!isChatAvailable) {
    return (
      <ChatUnavailable
        chatVisibleFrom={chatVisibleFrom}
        chatVisibleUntil={chatVisibleUntil}
        onBack={onBack}
      />
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Conversation Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="hover:bg-gray-200 p-1 rounded transition-colors"
            aria-label="Back to chat list"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 truncate">
              {otherPersonName}
            </div>
            <div className="text-sm text-gray-500 truncate">
              {currentChat?.eventType || 'Wedding'} • {currentChat?.venueName || 'Event'}
            </div>
          </div>
          {!isConnected && (
            <div className="text-xs text-amber-600 flex items-center gap-1">
              <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
              Connecting...
            </div>
          )}
          {isConnected && (
            <div className="text-xs text-green-600 flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              Online
            </div>
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div ref={scrollContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-red-500 text-sm">
            Error loading messages: {error}
          </div>
        ) : (
          <>
            <ChatMessages messages={messages} currentChat={currentChat} />
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <ChatInput
        onSendMessage={handleSendMessage}
        isSending={isSending}
        disabled={!isConnected || isLoading}
        bookingId={currentBookingId}
        threadId={resolvedThreadId}
      />
    </div>
  )
}
